/**
 * Hook to find active suppression rules that match a given security issue.
 * Used by the SuppressionBanner on the issue detail page.
 */

import { useMemo } from "react";
import { useSuppressionRules } from "./useSuppressionRules";
import type { SecurityIssue } from "@/types/security";
import type { SuppressionRule } from "@/types/suppressionRule";

const MAX_PATTERN_LENGTH = 200;

/**
 * Convert an fnmatch-style glob pattern to a RegExp.
 * Supports `*` (match anything) and `?` (match single char).
 */
function globToRegex(pattern: string): RegExp {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&");
  const regexStr = escaped.replace(/\*/g, ".*").replace(/\?/g, ".");
  return new RegExp(`^${regexStr}$`, "i");
}

function ruleMatchesIssue(rule: SuppressionRule, issue: SecurityIssue): boolean {
  // Skip expired rules
  if (rule.expires_at && new Date(rule.expires_at) < new Date()) return false;

  // All non-null criteria must match (AND logic)
  if (rule.match_issue_type && rule.match_issue_type !== issue.type) return false;
  if (rule.match_severity && rule.match_severity !== issue.severity) return false;
  if (rule.match_scanner && rule.match_scanner !== issue.scanner) return false;
  if (rule.match_title_pattern) {
    if (rule.match_title_pattern.length > MAX_PATTERN_LENGTH) return false;
    try {
      const regex = globToRegex(rule.match_title_pattern);
      if (!regex.test(issue.title)) return false;
    } catch {
      return false;
    }
  }
  return true;
}

export function useMatchingSuppressionRules(issue: SecurityIssue | undefined) {
  // Fetch up to 100 active rules to maximize matching coverage
  const { data, isLoading } = useSuppressionRules(1, true, 100);

  const matchingRules = useMemo(() => {
    if (!issue || !data?.data) return [];
    return data.data.filter((rule) => ruleMatchesIssue(rule, issue));
  }, [issue, data]);

  const truncated = data?.has_more === true;

  return { matchingRules, isLoading, truncated };
}
